// Spin the Bottle in the world, seen by everyone: the bottle (spinning to the
// server's result with an ease-out), a glowing ring on the chosen player's
// cushion, the current card floating over the circle, and names + scores
// over the occupied cushions. Table-local, floor origin.
//
// The spin itself is decided by the server (g.spin: who it lands on, how many
// turns, how long). Every client animates from wherever its bottle is now to
// the same final heading, so all players see it stop on the same cushion.
// gameBroadcast carries only the live, non-authoritative angle while the
// spinner (in VR) grabs and twists the bottle before flicking it.

import React, { useEffect, useMemo, useRef } from 'react';
import * as THREE from 'three';
import { useFrame } from '@react-three/fiber';
import { Billboard, Text } from '@react-three/drei';
import { useXR } from '@react-three/xr';
import { noRaycast } from '../../VR/vrUi';
import { useVRBuild } from '../../VR/build/vrBuildState';
import { useGameFx, sendGameFx } from '../gameBroadcast';
import type { Table3DProps } from '../types';
import type { BottleState } from '../../../../supabase/functions/games/bottle.ts';
import { BottleProp } from './BottleProp';
import { CUSHION_R, CUSHION_TOP, bottleRotationY, cushionAngle, cushionXZ, easeOut, spinEnd } from './bottleLayout';
import { nameOf, sendMove } from './bottleUi';

const BOTTLE_Y = 0.075;
const TEAL = '#00c2b3', GOLD = '#ffd25e';
const _v = new THREE.Vector3();

/** Heading (θ) of a table-local point, as seen from the middle. */
const headingOf = (x: number, z: number) => Math.atan2(x, -z);
const wrapPi = (a: number) => Math.atan2(Math.sin(a), Math.cos(a));

function CushionRing({ seat, color, pulse }: { seat: number; color: string; pulse: boolean }) {
  const ref = useRef<THREE.Mesh>(null);
  const [x, z] = cushionXZ(seat);
  useFrame(({ clock }) => {
    const m = ref.current;
    if (!m) return;
    const s = pulse ? 1 + Math.sin(clock.elapsedTime * 5) * 0.06 : 1;
    m.scale.set(s, s, 1);
    (m.material as THREE.MeshBasicMaterial).opacity = pulse ? 0.75 + Math.sin(clock.elapsedTime * 5) * 0.2 : 0.7;
  });
  return (
    <mesh ref={ref} position={[x, 0.02, z]} rotation={[-Math.PI / 2, 0, 0]} raycast={noRaycast as any} userData={{ collidable: false }}>
      <ringGeometry args={[0.3, 0.37, 40]} />
      <meshBasicMaterial color={color} transparent opacity={0.7} toneMapped={false} depthWrite={false} />
    </mesh>
  );
}

/** The spinning bottle. Owns the animation and (in VR, for the spinner) grab-and-flick. */
function SpinningBottle({ pid, worldId, tableId, gameId, g, canSpin }: { pid: string; worldId: string; tableId: string | null; gameId?: string; g: BottleState | null; canSpin: boolean }) {
  const root = useRef<THREE.Group>(null);
  const spinner = useRef<THREE.Group>(null);
  const presenting = useXR((s) => !!s.session);
  // Current heading, the running animation, and a local free-spin after a flick.
  const theta = useRef(g?.spin ? cushionAngle(g.spin.to) : 0);
  const anim = useRef<null | { from: number; to: number; start: number; ms: number }>(null);
  const free = useRef(0); // rad/s after a flick, until the server's spin arrives
  const seenSpin = useRef<string | undefined>(undefined); // `${gameId}:${n}`; undefined = never looked yet
  const drag = useRef<null | { id: number; offset: number; raw: number; samples: { t: number; a: number }[]; lastSent: number }>(null);
  const remote = useRef<number | null>(null);

  // A new spin from the server: animate from here to its result.
  const spin = g?.spin ?? null;
  const spinKey = spin ? `${gameId ?? ''}:${spin.n}` : null;
  useEffect(() => {
    if (!spin || !spinKey || seenSpin.current === spinKey) return;
    const firstLook = seenSpin.current === undefined;
    seenSpin.current = spinKey;
    // Walking up to a circle mid-game: show where it landed, don't replay it.
    if (firstLook && g?.phase !== 'spinning') { theta.current = cushionAngle(spin.to); return; }
    drag.current = null;
    remote.current = null;
    free.current = 0;
    const from = theta.current;
    anim.current = { from, to: spinEnd(from, spin.to, spin.turns), start: performance.now(), ms: spin.ms };
  }, [spinKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // Live angle while the spinner twists the bottle (from someone else's VR hands).
  useGameFx(worldId, tableId, 'bottle', (d) => {
    if (typeof d?.a !== 'number' || !Number.isFinite(d.a)) return;
    if ((d.n ?? null) !== (g?.spin?.n ?? null)) return; // stale (an older turn)
    remote.current = d.a;
  });

  useFrame((_, dt) => {
    const a = anim.current;
    if (a) {
      const t = (performance.now() - a.start) / a.ms;
      theta.current = a.from + (a.to - a.from) * easeOut(t);
      if (t >= 1) { anim.current = null; theta.current = a.to; }
    } else if (free.current) {
      theta.current += free.current * dt;
      free.current *= Math.exp(-1.2 * dt);
      if (Math.abs(free.current) < 0.3) free.current = 0;
    } else if (!drag.current && remote.current !== null && g?.phase === 'spin') {
      theta.current += wrapPi(remote.current - theta.current) * Math.min(1, dt * 12);
    }
    if (spinner.current) spinner.current.rotation.y = bottleRotationY(theta.current);
  });

  // ---- VR grab-and-flick (pinch the bottle, twist, let go) ----
  const localHeading = (p: THREE.Vector3) => {
    const r = root.current!;
    r.worldToLocal(_v.copy(p));
    return headingOf(_v.x, _v.z);
  };
  const grabbable = canSpin && presenting && !!tableId;
  useEffect(() => () => { if (drag.current) useVRBuild.getState().setHandling(false); }, []);

  return (
    <group ref={root}>
      <group ref={spinner} position={[0, BOTTLE_Y, 0]}>
        <BottleProp glow={canSpin} />
      </group>
      {/* Grab zone: a generous disc around the bottle, only for the spinner in VR. */}
      {grabbable && (
        <mesh
          position={[0, 0.09, 0]}
          name="vr-bottle-grab"
          userData={{ collidable: false }}
          onPointerDown={(e) => {
            e.stopPropagation();
            if (drag.current || anim.current) return;
            try { (e.target as any).setPointerCapture?.(e.pointerId); } catch { /* not capturable */ }
            const raw = localHeading(e.point);
            drag.current = { id: e.pointerId, offset: theta.current - raw, raw, samples: [{ t: performance.now(), a: theta.current }], lastSent: 0 };
            free.current = 0;
            useVRBuild.getState().setHandling(true);
          }}
          onPointerMove={(e) => {
            const d = drag.current;
            if (!d || e.pointerId !== d.id) return;
            e.stopPropagation();
            const raw = localHeading(e.point);
            const unwrapped = d.raw + wrapPi(raw - d.raw);
            d.raw = unwrapped;
            theta.current = unwrapped + d.offset;
            const now = performance.now();
            d.samples.push({ t: now, a: theta.current });
            while (d.samples.length > 2 && now - d.samples[0].t > 150) d.samples.shift();
            if (now - d.lastSent > 66 && worldId && tableId) {
              d.lastSent = now;
              sendGameFx(worldId, tableId, 'bottle', { a: Math.round(theta.current * 1000) / 1000, n: g?.spin?.n ?? null });
            }
          }}
          onPointerUp={(e) => {
            const d = drag.current;
            if (!d || e.pointerId !== d.id) return;
            e.stopPropagation();
            try { (e.target as any).releasePointerCapture?.(e.pointerId); } catch { /* released */ }
            drag.current = null;
            useVRBuild.getState().setHandling(false);
            const s = d.samples, first = s[0], last = s[s.length - 1];
            const dt = (last.t - first.t) / 1000;
            const w = dt > 0.01 ? (last.a - first.a) / dt : 0;
            if (Math.abs(w) > 4) {
              free.current = w;
              void sendMove(pid, { type: 'spin', power: Math.min(1, Math.abs(w) / 25) });
            }
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <cylinderGeometry args={[0.32, 0.32, 0.16, 24]} />
          <meshBasicMaterial transparent opacity={0} depthWrite={false} />
        </mesh>
      )}
    </group>
  );
}

/** The card (or the question) floating over the circle, facing whoever looks at it. */
function CardOverCircle({ title, color, text, sub }: { title: string; color: string; text: string; sub?: string }) {
  return (
    <Billboard position={[0, 1.75, 0]}>
      <mesh position={[0, -0.12, -0.01]} raycast={noRaycast as any}>
        <planeGeometry args={[2.1, 0.78]} />
        <meshBasicMaterial color="#120c1f" transparent opacity={0.72} depthWrite={false} toneMapped={false} />
      </mesh>
      <Text raycast={noRaycast} position={[0, 0.17, 0]} fontSize={0.12} color={color} outlineWidth={0.006} outlineColor="#000" anchorY="middle">{title}</Text>
      <Text raycast={noRaycast} position={[0, 0.06, 0]} fontSize={0.095} color="#ffffff" maxWidth={1.95} textAlign="center" anchorY="top" lineHeight={1.2}>{text}</Text>
      {sub && <Text raycast={noRaycast} position={[0, -0.44, 0]} fontSize={0.06} color="#cfc4e6" anchorY="middle">{sub}</Text>}
    </Billboard>
  );
}

export function BottleTable3D({ pid, worldId, table, seat }: Table3DProps) {
  const st = table?.state;
  const g = (st?.phase !== 'lobby' ? st?.g : null) as BottleState | null;
  const playing = st?.phase === 'playing' && !!g;
  const canSpin = playing && g!.phase === 'spin' && g!.spinner === seat;

  const seated = useMemo(() => (st ? st.seats.map((s, i) => (s ? i : -1)).filter((i) => i >= 0) : []), [st]);

  let over: React.ReactNode = null;
  if (st && g && playing) {
    const tName = nameOf(st, g, g.target);
    if (g.phase === 'choose') over = <CardOverCircle title={`${tName}!`} color={GOLD} text="Truth or Dare?" sub={g.mode === 'spin' && g.chooser !== g.target ? `${nameOf(st, g, g.chooser)} picks` : undefined} />;
    else if ((g.phase === 'card' || g.phase === 'cheer') && g.card) {
      over = <CardOverCircle title={g.card.type === 'dare' ? 'DARE' : 'TRUTH'} color={g.card.type === 'dare' ? '#ff7a6b' : '#6bc8ff'} text={g.card.text}
        sub={g.phase === 'cheer' ? `${tName} did it!${g.claps.length ? `  ·  ${g.claps.length} clap${g.claps.length === 1 ? '' : 's'}` : ''}` : `for ${tName}`} />;
    } else if (g.phase === 'spin') {
      over = <Billboard position={[0, 1.6, 0]}>
        <Text raycast={noRaycast} fontSize={0.1} color={TEAL} outlineWidth={0.006} outlineColor="#000">{`${nameOf(st, g, g.spinner)} spins next`}</Text>
      </Billboard>;
    }
  } else if (st?.phase === 'over' && st.g) {
    const fg = st.g as BottleState;
    const top = seated.map((i) => ({ i, s: fg.players[i]?.score ?? 0 })).sort((a, b) => b.s - a.s)[0];
    over = <CardOverCircle title="Game over" color={GOLD} text={top ? `${nameOf(st, fg, top.i)} leads with ${top.s} points` : 'Thanks for playing!'} />;
  } else if (st?.phase === 'lobby') {
    over = <Billboard position={[0, 1.5, 0]}>
      <Text raycast={noRaycast} fontSize={0.11} color="#ffffff" outlineWidth={0.006} outlineColor="#000" anchorY="bottom">Spin the Bottle</Text>
      <Text raycast={noRaycast} position={[0, -0.02, 0]} fontSize={0.07} color="#d7e3ee" outlineWidth={0.005} outlineColor="#000" anchorY="top">
        {`${seated.length}/12 on cushions — walk up to play`}
      </Text>
    </Billboard>;
  }

  return (
    <>
      <SpinningBottle pid={pid} worldId={worldId} tableId={table?.id ?? null} gameId={st?.gameId} g={playing ? g : null} canSpin={canSpin} />
      {playing && g && (g.phase === 'choose' || g.phase === 'card' || g.phase === 'cheer') && g.target !== null && (
        <CushionRing seat={g.target} color={GOLD} pulse />
      )}
      {playing && g && g.phase === 'spin' && <CushionRing seat={g.spinner} color={TEAL} pulse={false} />}
      {over}
      {/* Names (and scores) over the occupied cushions */}
      {st && seated.map((i) => {
        const [x, z] = cushionXZ(i, CUSHION_R + 0.08);
        const s = st.seats[i]!;
        const score = g?.players[i]?.score;
        const hot = playing && g && ((g.phase === 'spin' && g.spinner === i) || (g.phase !== 'spin' && g.phase !== 'spinning' && g.target === i));
        return (
          <Billboard key={i} position={[x, CUSHION_TOP + 1.05, z]}>
            <Text raycast={noRaycast} fontSize={0.07} color={hot ? (g!.phase === 'spin' ? TEAL : GOLD) : '#ffffff'} outlineWidth={0.005} outlineColor="#000" anchorY="bottom">
              {`${s.name.slice(0, 18)}${i === seat ? ' (you)' : ''}`}
            </Text>
            {score !== undefined && <Text raycast={noRaycast} position={[0, -0.015, 0]} fontSize={0.055} color="#d7e3ee" outlineWidth={0.004} outlineColor="#000" anchorY="top">{`${score} pts`}</Text>}
          </Billboard>
        );
      })}
    </>
  );
}

export default BottleTable3D;
